import React from "react";
import { withDesktopPageClass } from "../layout/desktopPage";
import {
  PAGE_LAYOUT,
  usesDesktopCanvas,
  withPageLayoutClass,
} from "../layout/pageLayout";
import { useDesktopNav } from "./navigation/DesktopNavContext";
import { DesktopBackLink } from "./navigation/ScreenBackButton";
import "./CrewPilotModuleShell.css";

/**
 * Shared module chrome (title, back, content).
 *
 * Layout (preferred): `layout="command"|"hub"|"form"|"phone"` — see
 * `src/layout/pageLayout.js` and docs/engineering/page_layout_reference.md.
 *
 * `compact` tightens the header for list/hub screens that should get users
 * into their content immediately (smaller title, no extra gap above content).
 *
 * Legacy: pass `desktopPage` to opt into the standard CrewPilot desktop canvas
 * (`src/layout/desktopPage.css`) — same tablet/desktop width as Dashboard.
 */
export function CrewPilotModuleShell({
  title,
  subtitle,
  sectionLabel,
  onBack,
  backLabel = "Back",
  titleClassName = "",
  headerActions,
  children,
  desktopPage = false,
  layout,
  compact = false,
  primary = false,
  crumbs,
  desktopBackLabel: desktopBackLabelProp,
}) {
  const desktopNav = useDesktopNav();
  // Desktop: primary destinations rely on the sidebar; drill-downs get "‹ Parent".
  const desktopBackLabel = desktopBackLabelProp || (backLabel && backLabel !== "Back" ? backLabel : desktopNav?.parentLabel || "Back");
  const resolvedLayout =
    layout || (desktopPage ? PAGE_LAYOUT.COMMAND : PAGE_LAYOUT.PHONE);
  const shellClassName = layout
    ? withPageLayoutClass("screen nx-screen cp-module-shell", resolvedLayout)
    : withDesktopPageClass(
        "screen nx-screen cp-module-shell",
        usesDesktopCanvas(resolvedLayout),
      );

  return (
    <div className={compact ? `${shellClassName} cp-module-shell--compact` : shellClassName}>
      {desktopNav && onBack && !primary ? (
        <DesktopBackLink onClick={onBack} label={desktopBackLabel} />
      ) : null}
      {desktopNav && crumbs && crumbs.filter(Boolean).length ? (
        <nav className="cp-crumbs" aria-label="Breadcrumb">
          <ol>
            {crumbs.filter(Boolean).map((crumb, index) => (
              <li key={`${index}-${crumb}`}>{crumb}</li>
            ))}
          </ol>
        </nav>
      ) : null}
      <div className="cp-module-header">
        {!desktopNav && onBack && (
          <button className="cockpit-back-btn" onClick={onBack}>
            {backLabel}
          </button>
        )}

        <div className="cp-module-header-text">
          <h1 className={["cp-module-title", titleClassName].filter(Boolean).join(" ")}>{title}</h1>

          {subtitle && (
            <div className="cp-module-subtitle">{subtitle}</div>
          )}
        </div>

        {headerActions}
      </div>

      {sectionLabel ? (
        <div className="cp-module-label">{sectionLabel}</div>
      ) : null}

      <div className="cp-module-content">{children}</div>
    </div>
  );
}
